<script setup lang="ts">
/**
 * Directors' Circular Resolution (Extension of Time) — M32015
 * · sole / multiple + Constitution / Articles / Third Schedule
 * · Private: circulate and lodge / lodge only (include_circulation)
 * · Public: lay before AGM and lodge (company_type === public)
 */
import { computed, inject } from 'vue';
import { EXTENSION_OF_TIME_DOC_CTX } from './extensionOfTimeContext';
import ExtensionOfTimeDocVarField from './ExtensionOfTimeDocVarField.vue';
import ExtensionSigners from './parts/ExtensionSigners.vue';

const ctx = inject(EXTENSION_OF_TIME_DOC_CTX)!;
const isMultiple = computed(() => ctx.flag('is_multiple_director_company'));
const includeCirculation = computed(() => ctx.flag('include_circulation'));
const basis = computed(() => ctx.choiceOf('resolution_basis') || 'constitution');
const isPublic = computed(() => (ctx.choiceOf('company_type') || 'private') === 'public');

function cycleBasis() {
  const next = basis.value === 'constitution' ? 'articles' : basis.value === 'articles' ? 'third_schedule' : 'constitution';
  ctx.setChoice('resolution_basis', next);
}

function setCompanyType(t: 'private' | 'public') {
  ctx.setChoice('company_type', t);
}

function togglePrivateAction() {
  if (isPublic.value) return;
  ctx.toggleFlag('include_circulation');
}

const secTitle = computed(() => {
  if (isPublic.value) {
    return 'APPLICATION FOR EXTENSION OF TIME TO LAY BEFORE THE ANNUAL GENERAL MEETING AND LODGE FINANCIAL STATEMENT & REPORT';
  }
  return includeCirculation.value
    ? 'APPLICATION FOR EXTENSION OF TIME TO CIRCULATE AND LODGE FINANCIAL STATEMENT & REPORT'
    : 'APPLICATION FOR EXTENSION OF TIME TO LODGE FINANCIAL STATEMENT & REPORT';
});

const actionPhrase = computed(() => {
  if (isPublic.value) return 'lay before the Company at its Annual General Meeting and lodge';
  return includeCirculation.value ? 'circulate and lodge' : 'lodge';
});
</script>

<template>
  <div class="eot-doc">
    <div class="doc-head">
      <div class="doc-company"><ExtensionOfTimeDocVarField field-key="company_name" /></div>
      <div class="doc-meta">Company Registration No: <ExtensionOfTimeDocVarField field-key="company_registration_no" /></div>
      <div class="doc-meta italic">(Incorporated in Malaysia)</div>
    </div>

    <div class="dcr-title">DIRECTORS' CIRCULAR RESOLUTION</div>

    <p class="type-row">
      <span
        class="doc-toggle type-chip"
        :class="{ on: !isPublic }"
        @click="setCompanyType('private')"
      >{{ !isPublic ? '☑' : '☐' }} Private Company</span>
      <span
        class="doc-toggle type-chip"
        :class="{ on: isPublic }"
        @click="setCompanyType('public')"
      >{{ isPublic ? '☑' : '☐' }} Public Company</span>
    </p>

    <p class="doc-p">
      <span
        class="doc-toggle"
        title="sole / multiple director"
        @click="ctx.toggleFlag('is_multiple_director_company')"
      >{{ isMultiple ? 'We, the undersigned, being the directors' : 'I, the undersigned, being the sole director' }}</span>
      of the above-mentioned Company for the time being, do resolve the following in writing pursuant to
      <template v-if="basis === 'third_schedule'">
        <span class="doc-toggle" title="resolution basis" @click="cycleBasis">Paragraph 15 of the Third Schedule of the Companies Act 2016</span>.
      </template>
      <template v-else>
        <span class="doc-toggle" title="resolution basis" @click="cycleBasis">{{ basis === 'articles' ? 'Article' : 'Clause' }}</span>
        <ExtensionOfTimeDocVarField field-key="constitution_clause_number" />
        <template v-if="basis === 'articles'"> of the Articles of Association</template>
        <template v-else> of the Company's Constitution</template>.
      </template>
    </p>

    <p class="doc-sec-title">
      <span
        class="doc-toggle"
        :title="isPublic ? 'public company — AGM wording' : 'include circulate / lodge only'"
        @click="togglePrivateAction"
      >{{ secTitle }}</span>
    </p>

    <p class="doc-p">
      THAT the company be hereby authorized to apply to the Registrar of Companies for the extension of time to
      <span
        class="doc-toggle"
        :title="isPublic ? 'public company — AGM wording' : 'circulate and lodge / lodge only'"
        @click="togglePrivateAction"
      >{{ actionPhrase }}</span>
      the Financial Statement &amp; Report for the year/period ended
      <ExtensionOfTimeDocVarField field-key="financial_year_end" />
      due to <ExtensionOfTimeDocVarField field-key="extension_reason" />.
    </p>

    <p class="doc-p">
      THAT the Secretary be authorized and instructed to forward and submit the application herein approved by the Board to the Companies Commission of Malaysia.
    </p>

    <p class="doc-lbl-title">BOARD OF DIRECTORS</p>
    <ExtensionSigners var-key="dcr" board-label="BOARD OF DIRECTORS" />
    <p class="doc-p" style="margin-top:14px"><b>Date:</b> <ExtensionOfTimeDocVarField field-key="dcr_date" /></p>
  </div>
</template>

<style src="./extensionOfTimeDoc.css"></style>
<style scoped>
.type-row { display: flex; flex-wrap: wrap; align-items: center; gap: 10px; margin: 4px 0 10px; justify-content: center; }
.type-chip { font-size: 12px; opacity: .55; }
.type-chip.on { opacity: 1; }
</style>
